import React, { useEffect, useState } from "react";
import { Box, Chip, InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import CAMHeader from "../../../components/Header/CAMHeader";
import CAMDrawer from "../../../components/LeftSideBar/CAMDrawer";
import InvestigatorContainer from "../../../components/contsiners/InvestigatorContainer";
import DropdownsBar from "../../analytics/header/ProductSiteUnitSelector";
import Pagination from "../../../components/Pagination";
import Loader from "../../../components/Loader/loader";
import SnackBar from "../../../components/Snackbars/CustomizedSnackbars";
import * as review from "../../../redux/review/action";
import CopyHistoryTable from "./CopyHistoryTable";
import TabsNav from "../../analytics/header/BatchTabsNav";
import HistoricalBatchMetadataView from "./HistoricalBatchMetadataView";
import { CURRENT_TEMPLATE_VERSION_LABEL } from "../../../constants/investigationAnalysisReview";

const getHistoryRequestKey = (unitOpId, page, search) =>
  [unitOpId || "", page, search || ""].join(":");

const CopyHistory = () => {
  const dispatch = useDispatch();
  const { unitOfOperation } = useSelector(
    (state) => state.shared?.productSitesUopValues || {},
  );
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadedRequestKey, setLoadedRequestKey] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [currentTemplateVersion, setCurrentTemplateVersion] = useState(null);
  const [history, setHistory] = useState({
    records: [],
    totalRecords: 0,
    pageSize: 10,
  });
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "warning",
  });

  const loadHistory = async (nextPage = 1) => {
    if (!unitOfOperation?.id) {
      setLoadedRequestKey(null);
      setCurrentTemplateVersion(null);
      setHistory({ records: [], totalRecords: 0, pageSize: 10 });
      setSnack({
        open: true,
        message: "Please select Unit Operation.",
        severity: "warning",
      });
      return;
    }
    setLoading(true);
    try {
      const response = await dispatch(
        review.getCopiedBatches({
          unit_op_id: unitOfOperation.id,
          page: nextPage,
          page_size: 10,
          ...(debouncedSearch && { search: debouncedSearch }),
        }),
      );
      const data = response?.data || {};
      setCurrentTemplateVersion(data.latest_template?.template_version ?? null);
      setHistory({
        records: data.records || [],
        totalRecords: data.total_records || 0,
        pageSize: data.page_size || 10,
      });
      setLoadedRequestKey(
        getHistoryRequestKey(unitOfOperation.id, nextPage, debouncedSearch),
      );
    } catch (error) {
      setLoadedRequestKey(null);
      setCurrentTemplateVersion(null);
      setSnack({
        open: true,
        message:
          error?.response?.data?.message || "Unable to fetch copied batches.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewMetadata = async (row) => {
    const ingestedFileId = row?.ingested_file_id;
    if (!row?.batch_id || !unitOfOperation?.id || !ingestedFileId) {
      setSnack({
        open: true,
        message: "Batch metadata identifiers are unavailable.",
        severity: "warning",
      });
      return;
    }
    setSelectedBatch(row);
    setMetadata(null);
    setMetadataLoading(true);
    try {
      const response = await dispatch(
        review.getHistoricalBatchMetadata({
          batch_id: row.batch_id,
          unit_op_id: unitOfOperation.id,
          ingested_file_id: ingestedFileId,
        }),
      );
      setMetadata(response?.data?.data || response?.data || null);
    } catch (error) {
      setSelectedBatch(null);
      setSnack({
        open: true,
        message:
          error?.response?.data?.message || "Unable to load batch metadata.",
        severity: "error",
      });
    } finally {
      setMetadataLoading(false);
    }
  };
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
    loadHistory(1);
  }, [unitOfOperation?.id, debouncedSearch]);

  return (
    <Box>
      <CAMHeader />
      <CAMDrawer />
      <InvestigatorContainer>
        <DropdownsBar />
        {selectedBatch ? (
          metadataLoading ? (
            <Loader />
          ) : (
            <HistoricalBatchMetadataView
              data={metadata}
              versionNumber={
                selectedBatch.version_label ||
                (selectedBatch.version_number
                  ? `Version-${selectedBatch.version_number}`
                  : "")
              }
              onBack={() => {
                setSelectedBatch(null);
                setMetadata(null);
              }}
            />
          )
        ) : (
          <>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ borderBottom: "1px solid #E4E7EC", px: 1, py: 1 }}
            >
              <TabsNav />
              {currentTemplateVersion !== null && (
                <Chip
                  className="current-template-version"
                  label={`${CURRENT_TEMPLATE_VERSION_LABEL}: ${currentTemplateVersion}`}
                  variant="outlined"
                />
              )}
              <TextField
                size="small"
                placeholder="Search by Batch ID"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ width: 220 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box
              mt={1}
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              height="calc(100vh - 165px)"
            >
              <CopyHistoryTable
                records={history.records}
                page={page}
                pageSize={history.pageSize}
                loading={loading}
                showEmptyState={
                  loadedRequestKey ===
                  getHistoryRequestKey(
                    unitOfOperation?.id,
                    page,
                    debouncedSearch,
                  )
                }
                onView={handleViewMetadata}
              />
              {loading && <Loader />}
              <Pagination
                count={Math.ceil(history.totalRecords / history.pageSize)}
                page={page}
                onChange={(_, value) => {
                  setPage(value);
                  loadHistory(value);
                }}
              />
            </Box>
          </>
        )}{" "}
        {snack.open && (
          <SnackBar
            className="popup"
            handleClose={() => setSnack((value) => ({ ...value, open: false }))}
            severity={snack.severity}
            state={snack.open}
            text={snack.message}
            autoHide={false}
          />
        )}
      </InvestigatorContainer>
    </Box>
  );
};

export default CopyHistory;
